//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { Document } from './components/Document';

// === API ===
import {emit, on, removeListener, sendMessageData} from 'api/prokey/prokey.websocket';

//#endregion

const DocMemo = React.memo( Document );

//-----------------------------------------------------------------
export function Thumbnails( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { fill } = props;

  const theme = useTheme();
  const panel_ref              = useRef( null );
  
  const [ documents, setDocuments ] = useState( { items: {}, order: [] } );
  const [    images, setImages    ] = useState( { items: {}, order: [] } );
  const document_container_ref = useRef( null );
  const mouse_y_ref            = useRef( 0 );
  const raf_ref                = useRef( 0 );
  const last_user_scroll_ts_ref = useRef( 0 );
  const is_pointer_down_ref      = useRef( false );

  const [ selected_thumb_id, setSelectedThumbId ] = useState( null );

  //#endregion

  //#region [STYLES]

  const _styles = {
    thumb_paper: {
      gridColumn: 'span ' + fill || 2,
      overflow : 'auto'     ,
      position : 'relative' ,
      height   : '100%',
      width    : '100%',
      boxShadow: 0,
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    document_container: {
      height: '100%', width: '100%', overflow: 'visible',
    }
  }

  //#endregion
  
  //#region [EFFECT]

  useEffect( () => {

    //-----------------------------------------------------------------
    function handleDocuments(data) {
      //-----------------------------------------------------------------
      setDocuments( data );
    }

    //-----------------------------------------------------------------
    function handleImages(data) {
      //-----------------------------------------------------------------
      setImages( data );
    }

    //-----------------------------------------------------------------
    function handleImageUpdates( data ) {
      //-----------------------------------------------------------------
      const temp = { ...images };
      if( data.images?.order ) temp.order = data.images.order;
      
      data.images?.update?.forEach( image => {
        emit( 'IMAGE-'+ image.id, image );
      });
    }

    //-----------------------------------------------------------------
    function handleClear(data) {
      //-----------------------------------------------------------------
      setImages   ( { items: {}, order: [] } );
      setDocuments( { items: {}, order: [] } );
    }

    on( 'TASK_DOCUMENTS' , handleDocuments    );
    on( 'TASK_IMAGES'    , handleImages       );
    on( 'IMAGE_UPDATE'   , handleImageUpdates );
    on( 'THUMBS_CLEAR'   , handleClear        );

    return () => {
      removeListener( 'TASK_DOCUMENTS' , handleDocuments    );
      removeListener( 'TASK_IMAGES'    , handleImages       );
      removeListener( 'IMAGE_UPDATE'   , handleImageUpdates );
      removeListener( 'THUMBS_CLEAR'   , handleClear        );
    }

  }, [] );
      
  useEffect( () => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData( 'TASK_VIEW_THUMBS_UNSUBSCRIBE' );
    }

    sendMessageData( 'TASK_VIEW_THUMBS_SUBSCRIBING' );

    window.addEventListener('beforeunload', unsubscribe );
    return () => {
      window.removeEventListener('beforeunload', unsubscribe );
    }
  }, [] );
  
  useEffect( () => {
    function _handleReconnect(data) {
      sendMessageData( 'TASK_VIEW_THUMBS_SUBSCRIBING', data );
    }

    on( 'connection', _handleReconnect );

    return () => {
      removeListener( 'connection', _handleReconnect );
    }
  }, [] );
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function handleDocumentUpdates( data ) {
      //-----------------------------------------------------------------
      try {
        const temp = { ...documents };

        // === DOCUMENTS
        if( data.documents?.order ) temp.order = data.documents.order;

        data.documents?.create?.forEach( document => {
          temp.items[ document.Id ] = document;
        });
        data.documents?.update?.forEach( document => {
          document.timestamp = Date.now();
          temp.items[ document.Id ] = document;
        });
        data.documents?.remove?.forEach( id => {   
          delete temp.items[ id ];
        });
        setDocuments( temp );
      } catch (error) {
        console.warn( error );
      }
    }

    on( 'DOCUMENT_UPDATE' , handleDocumentUpdates );

    return () => {
      removeListener( 'DOCUMENT_UPDATE' , handleDocumentUpdates );
    }
  }, [documents] );

  useEffect( () => {

    //-----------------------------------------------------------------
    function onMouseMove( e ) {
      //-----------------------------------------------------------------
      mouse_y_ref.current = e.clientY;
    }

    //-----------------------------------------------------------------
    function onWheel() {
      //-----------------------------------------------------------------
      last_user_scroll_ts_ref.current = Date.now();
    }

    //-----------------------------------------------------------------
    function onPointerDown() {
      //-----------------------------------------------------------------
      // Covers scrollbar drag (no wheel event)
      is_pointer_down_ref.current = true;
      last_user_scroll_ts_ref.current = Date.now();
    }

    //-----------------------------------------------------------------
    function onPointerUp() {
      //-----------------------------------------------------------------
      is_pointer_down_ref.current = false;
    }

    //-----------------------------------------------------------------
    function onScroll() {
      //-----------------------------------------------------------------
      if( raf_ref.current ) return;

      raf_ref.current = window.requestAnimationFrame( () => {
        raf_ref.current = 0;

        const container = panel_ref.current;
        if( !container ) return;

        const rect = container.getBoundingClientRect();
        const centerX = rect.left + ( rect.width / 2 );
        const y = mouse_y_ref.current;

        // Ignore programmatic scrolls (e.g. scrollIntoView on selection)
        // Only react when the user has scrolled via wheel/trackpad very recently,
        // or user is currently dragging (scrollbar).
        if( !is_pointer_down_ref.current && ( Date.now() - last_user_scroll_ts_ref.current > 150 ) ) return;

        // Only react when the mouse is vertically over the panel
        if( y < rect.top || y > rect.bottom ) return;

        let el = document.elementFromPoint( centerX, y );
        if( !el ) return;

        // Walk up to the nearest thumbnail wrapper
        const thumbWrapper = el.closest( '[data-thumb-id]' );
        if( !thumbWrapper ) return;

        const thumbId = thumbWrapper.getAttribute( 'data-thumb-id' );
        if( thumbId && thumbId !== selected_thumb_id ) {
          setSelectedThumbId( thumbId );
        }
      });
    }

    window.addEventListener( 'mousemove', onMouseMove, { passive: true } );

    const container = panel_ref.current;
    if( container ) {
      container.addEventListener( 'pointerdown', onPointerDown, { passive: true } );
      container.addEventListener( 'wheel',       onWheel      , { passive: true } );
      container.addEventListener( 'scroll',      onScroll     , { passive: true } );
    }
    window.addEventListener( 'pointerup', onPointerUp, { passive: true } );

    return () => {
      window.removeEventListener( 'mousemove', onMouseMove );

      const c = panel_ref.current;
      if( c ) {
        c.removeEventListener( 'pointerdown', onPointerDown );
        c.removeEventListener( 'wheel',       onWheel       );
        c.removeEventListener( 'scroll',      onScroll      );
      }
      window.removeEventListener( 'pointerup', onPointerUp );

      if( raf_ref.current ) {
        window.cancelAnimationFrame( raf_ref.current );
        raf_ref.current = 0;
      }
    }

  }, [ selected_thumb_id ] );

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const thumb_documents = documents.order.map( displayDocument );
    
    return (
      <Box ref={panel_ref} sx={_styles.thumb_paper}>
        <Box ref={document_container_ref} sx={_styles.document_container} >
          { thumb_documents }
        </Box>
      </Box>
    );
  }
  
  //-----------------------------------------------------------------
  function displayDocument( id, index ) {
    //-----------------------------------------------------------------
    const document = documents.items[id];    
    if( !document ) {
      console.log( `Failed to get document with id [${id}].`);
    }
    const timestamp = document?.timestamp;    
    return (
      <Box key={ 'd' + id } data-doc-id={ id }>
        <DocMemo
          key={ 'd' + id }
          id={ id }
          timestamp={ timestamp }
          index={ index }
          images={ images.order }
          scroll_selected_id={ selected_thumb_id }
        />
      </Box>  
      );
    }

  //#endregion

  return display();
}
